import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  Checkbox,
  TextField,
  IconButton,
  Divider,
  Paper,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  AppBar,
  Toolbar,
  InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
  FaRegClock, 
  FaClipboardList, 
  FaUserPlus, 
  FaExclamationCircle, 
  FaBan,
  FaSun,
  FaMoon,
  FaCloudSun
} from 'react-icons/fa';
import {
  Users,
  RefreshCw,
  Search,
  X,
  ChevronRight,
} from 'lucide-react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import OnboardEmployee from './OnboardEmployee';
import SimpleBatchArchiveEmployees from './SimpleBatchArchiveEmployees';
import SimpleReinstateEmployees from './SimpleReinstateEmployees';
import Suspend from './Suspend';
import DisciplinaryRecords from './DisciplinaryRecords';
import { getPayrollData, savePayrollData } from './storageUtils';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { AuthContext } from '../context/AuthContext';
import PayChangeAcknowledgement from './PayChangeAcknowledgement';
import Alert from '@mui/material/Alert';
import ActivityFeed from './ActivityFeed';
import GreetingCard from './GreetingCard';
import RoleBasedView from './RoleBasedView';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL
import { API_URL } from '../utils/apiConfig';

console.log("Current environment:", process.env.NODE_ENV);
console.log("API_URL", API_URL);

const Dashboard = () => {
  const { userRole, user, userDetails } = useContext(AuthContext);
  const [recentUpdates, setRecentUpdates] = useState({ recentNotes: [], recentStopPayments: [] });
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [openModal, setOpenModal] = useState(null);
  const [payrollData, setPayrollData] = useState(() => getPayrollData());
  const [filteredData, setFilteredData] = useState([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [newTodo, setNewTodo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAcknowledgement, setShowAcknowledgement] = useState(false);
  const [pendingAcknowledgements, setPendingAcknowledgements] = useState(0);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [pendingPayChanges, setPendingPayChanges] = useState([]);
  const [recentAcknowledgements, setRecentAcknowledgements] = useState([]);
  
  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: 'Good Morning', icon: <FaSun size={24} color="#ff9800" /> };
    if (hour < 18) return { text: 'Good Afternoon', icon: <FaCloudSun size={24} color="#2196f3" /> };
    return { text: 'Good Evening', icon: <FaMoon size={24} color="#673ab7" /> };
  };
  
  const greeting = getGreeting();
  
  // Fetch pending acknowledgements for admins, managers, and Mary Jane
  const checkPendingAcknowledgements = async () => {
      // Managers should still fetch (to see details), but banner remains admin/MJ only
      const canViewAcknowledgements = userDetails && (
        userDetails.email === 'maryjane@dailey.llc' ||
        userRole === 'admin' ||
        userRole === 'manager'
      );
      
      if (canViewAcknowledgements) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_URL}/notifications/pending`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            const totalPending = (data.notifications?.length || 0) + (data.payChanges?.length || 0);
            setPendingAcknowledgements(totalPending);
            setPendingNotifications(data.notifications || []);
            setPendingPayChanges(data.payChanges || []);
          } else {
            setPendingAcknowledgements(0);
            setPendingNotifications([]);
            setPendingPayChanges([]);
          }
        } catch (error) {
          console.error('Failed to check pending acknowledgements:', error);
          setPendingAcknowledgements(0);
          setPendingNotifications([]);
          setPendingPayChanges([]);
        }
      }
  };

  // Initial/role-change load of pending acknowledgements
  useEffect(() => {
    checkPendingAcknowledgements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, userRole]);

  // Load recently acknowledged items (for Mary Jane/admin/manager/impersonation)
  useEffect(() => {
    const loadRecent = async () => {
      let allow = false;
      let isImp = false;
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          isImp = !!payload.isImpersonating;
        }
      } catch (_) {}
      allow = !!userDetails && (userDetails.email === 'maryjane@dailey.llc' || userRole === 'admin' || userRole === 'manager' || isImp);
      if (!allow) return;
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API_URL}/notifications/acknowledgements/recent?limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setRecentAcknowledgements(data.acknowledgements || []);
        } else {
          setRecentAcknowledgements([]);
        }
      } catch (e) {
        setRecentAcknowledgements([]);
      }
    };
    loadRecent();
  }, [userDetails, userRole]);
  
  useEffect(() => {
    fetchRecentUpdates();
  }, []);
  
  const fetchRecentUpdates = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/recent_updates`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch recent updates");
      }

      const data = await response.json();
      setRecentUpdates(data);
    } catch (err) {
      console.error("Error fetching recent updates:", err);
      setError("Failed to load recent updates.");
    } finally {
      setRefreshing(false);
    }
  };
    
  useEffect(() => {
    const dataWithContent = payrollData.filter(
      (payPeriod) => (payPeriod.todos?.length > 0 || payPeriod.notes?.length > 0)
    );
    setFilteredData(dataWithContent);
  }, [payrollData]);
    
  // If userRole is employee, show a message and exit early
  if (userRole === 'employee') {
    return <Typography>You are not authorized to view this page.</Typography>;
  }

  const handleOpenModal = (action) => setOpenModal(action);
  const handleCloseModal = () => setOpenModal(null);

  const openPayPeriodModal = (payPeriod) => setSelectedPayPeriod(payPeriod);
  const closePayPeriodModal = () => {
    setSelectedPayPeriod(null);
    setNewNote('');
    setNewTodo('');
  };

  const handleAddTodo = () => {
    if (newTodo.trim() && selectedPayPeriod) {
      const updatedTodos = [...(selectedPayPeriod.todos || []), { task: newTodo, completed: false }];
      setSelectedPayPeriod({ ...selectedPayPeriod, todos: updatedTodos });
      setNewTodo('');
    }
  };

  const handleAddNote = () => {
    if (newNote.trim() && selectedPayPeriod) {
      const updatedNotes = [...(selectedPayPeriod.notes || []), { note: newNote, date: new Date().toISOString() }];
      setSelectedPayPeriod({ ...selectedPayPeriod, notes: updatedNotes });
      setNewNote('');
    }
  };

  const handleTodoToggle = (index) => {
    if (!selectedPayPeriod) return;
    const updatedTodos = selectedPayPeriod.todos.map((todo, idx) =>
      idx === index ? { ...todo, completed: !todo.completed } : todo
    );
    setSelectedPayPeriod({ ...selectedPayPeriod, todos: updatedTodos });
  };

  const saveNotesAndTodos = () => {
    const updatedData = payrollData.map((period) =>
      period.startDate === selectedPayPeriod.startDate && period.endDate === selectedPayPeriod.endDate
        ? { ...selectedPayPeriod }
        : period
    );
    setPayrollData(updatedData);
    savePayrollData(updatedData);
    closePayPeriodModal();
  };

  const handleOnboardSave = (employeeInfo, selectedForms) => {
    console.log('Employee Info:', employeeInfo);
    console.log('Selected Forms:', selectedForms);
  };

  const handleEditTodo = (index) => {
    const todoToEdit = selectedPayPeriod.todos[index];
    setNewTodo(todoToEdit.task);
    handleTodoToggle(index); // optional: toggle editing state
  };
  
  const handleDeleteTodo = (index) => {
    const updatedTodos = selectedPayPeriod.todos.filter((_, idx) => idx !== index);
    setSelectedPayPeriod({ ...selectedPayPeriod, todos: updatedTodos });
  };
  
  const handleEditNote = (index) => {
    const noteToEdit = selectedPayPeriod.notes[index];
    setNewNote(noteToEdit.note);
  };
  
  const handleDeleteNote = (index) => {
    const updatedNotes = selectedPayPeriod.notes.filter((_, idx) => idx !== index);
    setSelectedPayPeriod({ ...selectedPayPeriod, notes: updatedNotes });
  };

  return (
    <>
      <Box
        sx={{
          bgcolor: "#F9FAFB",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Pending Acknowledgements Alert for admins (and Mary Jane) only */}
        {userDetails && (userDetails.email === 'maryjane@dailey.llc' || userRole === 'admin') && pendingAcknowledgements > 0 && (
          <Alert 
            severity="warning" 
            sx={{ 
              mx: 2, 
              mt: 2,
              cursor: 'pointer'
            }}
            onClick={() => setShowAcknowledgement(true)}
          >
            You have {pendingAcknowledgements} employee change{pendingAcknowledgements > 1 ? 's' : ''} that require acknowledgement. Click here to review.
          </Alert>
        )}

        {/* Welcome Header - No Container */}
        <GreetingCard />
        

        {/* Main Content - Flex Layout */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            px: { xs: 2, md: 3 },
            pb: 3,
          }}
        >
          {/* Left Side (Feed) - Mobile: Full Width, Desktop: 2/3 Width */}
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '2 1 66%' },
              order: { xs: 2, md: 1 },
            }}
          >
            <ActivityFeed />
          </Box>

          {/* Right Side (Actions) - Mobile: Full Width, Desktop: 1/3 Width */}
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 33%' },
              order: { xs: 1, md: 2 },
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {/* Employee Actions Section (first) */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB',
              }}
            >
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: '#d6e8ed',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1F2937',
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <Users size={20} color="#2563EB" style={{ marginRight: 8 }} />
                  Employee Actions
                </Typography>
              </Box>
              
              <List sx={{ p: 2 }}>
                {[
                  { title: "Onboarding", icon: <FaUserPlus size={16} />, color: "#4caf50" },
                  { title: "Archive", icon: <FaExclamationCircle size={16} />, color: "#ff9800" },
                  { title: "Reinstate", icon: <FaUserPlus size={16} />, color: "#2196f3" },
                ].map((item, index) => (
                  <ListItem
                    key={item.title}
                    button
                    onClick={() => handleOpenModal(item.title)}
                    sx={{
                      mb: index === 2 ? 0 : 2,
                      borderRadius: 1,
                      p: 2,
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      bgcolor: "#fff",
                      border: '1px solid #E5E7EB',
                      '&:hover': { 
                        bgcolor: '#F9FAFB',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                  >
                    <Box 
                      component="span"
                      sx={{ 
                        mr: 2, 
                        color: item.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {item.icon}
                    </Box>
                    <ListItemText 
                      primary={item.title}
                      primaryTypographyProps={{
                        sx: { fontWeight: 500, color: '#1F2937' }
                      }}
                    />
                    <ChevronRight size={18} color="#9CA3AF" />
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Compact Pending Changes Preview */}
            {pendingAcknowledgements > 0 && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB',
                }}
              >
                <Box 
                  sx={{ 
                    p: 2, 
                    backgroundColor: '#fff7ed',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#7c2d12' }}>
                    Pending Acknowledgements
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Showing recent items. Click to review all.
                  </Typography>
                </Box>
                <List sx={{ p: 1 }}>
                  {[...pendingPayChanges].slice(0, 5).map((c) => (
                    <ListItem key={`chg-${c.id}`} sx={{ py: 1 }} onClick={() => setShowAcknowledgement(true)} button>
                      <ListItemText 
                        primary={`${c.firstName} ${c.lastName} • ${String(c.changeType || '').replace(/_/g,' ').toUpperCase()}`}
                        secondary={new Date(c.created_at).toLocaleString()}
                      />
                    </ListItem>
                  ))}
                  {[...pendingNotifications].slice(0, 3).map((n) => (
                    <ListItem key={`not-${n.id}`} sx={{ py: 1 }} onClick={() => setShowAcknowledgement(true)} button>
                      <ListItemText 
                        primary={n.title}
                        secondary={new Date(n.created_at).toLocaleString()}
                      />
                    </ListItem>
                  ))}
                </List>
              <Box sx={{ px: 2, pb: 2 }}>
                <Button variant="outlined" size="small" onClick={() => setShowAcknowledgement(true)}>
                  Review all ({pendingAcknowledgements})
                </Button>
              </Box>
            </Paper>
          )}

            {/* Recently Reviewed Section */}
            {recentAcknowledgements.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #E5E7EB',
                }}
              >
                <Box 
                  sx={{ 
                    p: 2, 
                    backgroundColor: '#ecfdf5',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#065f46' }}>
                    Recently Reviewed
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    The latest acknowledgements in the system
                  </Typography>
                </Box>
                <List sx={{ p: 1 }}>
                  {recentAcknowledgements.map((r) => (
                    <ListItem key={`ack-${r.id}`} sx={{ py: 1 }}>
                      <ListItemText 
                        primary={`${r.firstName} ${r.lastName} • ${String(r.changeType || '').replace(/_/g,' ').toUpperCase()}`}
                        secondary={`Reviewed by ${r.acknowledgedBy || 'Unknown'} at ${r.acknowledgedAt ? new Date(r.acknowledgedAt).toLocaleString() : ''}`}
                      />
                    </ListItem>
                  ))}
                </List>
                {/* CSV export temporarily removed until it is fully tested */}
              </Paper>
            )}
            
            {/* Employee Actions Section (removed duplicate) */}
            {/* <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #E5E7EB',
              }}
            >
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: '#d6e8ed',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1F2937',
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <Users size={20} color="#2563EB" style={{ marginRight: 8 }} />
                  Employee Actions
                </Typography>
              </Box>
              
              <List sx={{ p: 2 }}>
                {[
                  { title: "Onboarding", icon: <FaUserPlus size={16} />, color: "#4caf50" },
                  { title: "Archive", icon: <FaExclamationCircle size={16} />, color: "#ff9800" },
                  { title: "Reinstate", icon: <FaUserPlus size={16} />, color: "#2196f3" },
                  // Suspension hidden but not deleted
                  // { title: "Suspend", icon: <FaBan size={16} />, color: "#f44336" },
                ].map((item, index) => (
                  <ListItem
                    key={item.title}
                    button
                    onClick={() => handleOpenModal(item.title)}
                    sx={{
                      mb: index === 2 ? 0 : 2,  // Last item has no margin bottom
                      borderRadius: 1,
                      p: 2,
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      bgcolor: "#fff",
                      border: '1px solid #E5E7EB',
                      '&:hover': { 
                        bgcolor: '#F9FAFB',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                  >
                    <Box 
                      component="span"
                      sx={{ 
                        mr: 2, 
                        color: item.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {item.icon}
                    </Box>
                    <ListItemText 
                      primary={item.title}
                      primaryTypographyProps={{
                        sx: { fontWeight: 500, color: '#1F2937' }
                      }}
                    />
                    <ChevronRight size={18} color="#9CA3AF" />
                  </ListItem>
                ))}
              </List>
            </Paper> */}
          </Box>
        </Box>
      </Box>

      {/* Pay Period Modal */}
      <Dialog
        open={Boolean(selectedPayPeriod)}
        onClose={closePayPeriodModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : '80%',
            height: isMobile ? '100%' : 'auto',
            margin: isMobile ? 0 : 'auto',
            borderRadius: isMobile ? 0 : 2,
            maxHeight: '100%',
          },
        }}
      >
        <DialogContent
          sx={{
            p: isMobile ? 2 : 3,
            overflowY: 'auto',
          }}
        >
          {selectedPayPeriod && (
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 'bold', mb: 2, textAlign: 'left' }}
              >
                To-Dos
              </Typography>
              <List>
                {selectedPayPeriod.todos.map((todo, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: todo.completed ? '#e8f5e9' : '#fff',
                      borderRadius: 1,
                      mb: 1,
                      px: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={todo.completed}
                        onChange={() => handleTodoToggle(index)}
                      />
                      <ListItemText
                        primary={
                          <Typography
                            sx={{
                              textDecoration: todo.completed ? 'line-through' : 'none',
                              color: todo.completed ? 'gray' : 'inherit',
                            }}
                          >
                            {todo.task}
                          </Typography>
                        }
                      />
                    </Box>
                    <Box>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditTodo(index)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteTodo(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
              <Box display="flex" alignItems="center" mt={1}>
                <TextField
                  placeholder="Add new to-do"
                  value={newTodo}
                  onChange={(e) => setNewTodo(e.target.value)}
                  fullWidth
                  size="small"
                />
                <IconButton color="primary" onClick={handleAddTodo} sx={{ ml: 1 }}>
                  <AddCircleOutlineIcon />
                </IconButton>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography
                variant="h6"
                sx={{ fontWeight: 'bold', mb: 2, textAlign: 'left' }}
              >
                Notes
              </Typography>
              <List>
                {selectedPayPeriod.notes.map((note, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      bgcolor: index === 0 ? '#e3f2fd' : '#f5f5f5',
                      borderRadius: 1,
                      mb: 1,
                      px: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {new Date(note.date).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2">{note.note}</Typography>
                    </Box>
                    <Box>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditNote(index)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteNote(index)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
              <Box display="flex" alignItems="center" mt={1}>
                <TextField
                  placeholder="Add new note"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  fullWidth
                  size="small"
                />
                <IconButton color="primary" onClick={handleAddNote} sx={{ ml: 1 }}>
                  <AddCircleOutlineIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: isMobile ? 1 : 3,
            justifyContent: 'flex-end',
          }}
        >
          <Button 
            onClick={closePayPeriodModal} 
            color="secondary"
            sx={{ 
              color: '#4B5563', 
              borderColor: '#D1D5DB',
              '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' },
              textTransform: 'none',
              mr: 1
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={saveNotesAndTodos} 
            variant="contained" 
            color="primary"
            sx={{ 
              bgcolor: '#2563EB',
              '&:hover': { bgcolor: '#1D4ED8' },
              textTransform: 'none'
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee Action Modals */}
      {['Onboarding', 'Archive', 'Reinstate', 'Disciplinary'].map((action) => {
        const isSimplified = action === 'Archive' || action === 'Reinstate' || action === 'Onboarding';
        
        return (
          <Dialog
            key={action}
            open={openModal === action}
            onClose={() => !processing && handleCloseModal()}
            fullWidth={!(isSimplified && isMobile)}
            fullScreen={isSimplified && isMobile}
            maxWidth={(isSimplified && isMobile) ? false : "lg"}
            PaperProps={{
              sx: {
                width: (isSimplified && isMobile) ? '100vw' : (isMobile ? '100%' : '60%'),
                height: (isSimplified && isMobile) ? 'auto' : (isMobile ? '100%' : 'auto'),
                margin: (isSimplified && isMobile) ? '0 !important' : 0,
                borderRadius: (isSimplified && isMobile) ? '0 !important' : (isMobile ? 0 : 2),
                maxHeight: (isSimplified && isMobile) ? '100vh' : '100%',
                maxWidth: (isSimplified && isMobile) ? '100vw !important' : 'none',
                minHeight: (isSimplified && isMobile) ? 'auto' : 'auto',
                minWidth: (isSimplified && isMobile) ? '100vw !important' : 'auto',
                position: 'fixed',
                top: (isSimplified && isMobile) ? '0 !important' : 'auto',
                left: (isSimplified && isMobile) ? '0 !important' : 'auto',
                right: (isSimplified && isMobile) ? '0 !important' : 'auto',
                bottom: (isSimplified && isMobile) ? '0 !important' : 'auto',
                transform: (isSimplified && isMobile) ? 'none !important' : 'auto',
                // Override MUI Paper classes aggressively
                '&.MuiPaper-root': {
                  borderRadius: (isSimplified && isMobile) ? '0 !important' : undefined,
                },
                '&.MuiPaper-rounded': {
                  borderRadius: (isSimplified && isMobile) ? '0 !important' : undefined,
                },
                '&.MuiPaper-elevation': {
                  borderRadius: (isSimplified && isMobile) ? '0 !important' : undefined,
                },
                '&.MuiPaper-elevation24': {
                  borderRadius: (isSimplified && isMobile) ? '0 !important' : undefined,
                },
                '&.MuiDialog-paper': {
                  borderRadius: (isSimplified && isMobile) ? '0 !important' : undefined,
                  margin: (isSimplified && isMobile) ? '0 !important' : undefined,
                },
                '&.MuiDialog-paperScrollPaper': {
                  maxHeight: (isSimplified && isMobile) ? '100vh !important' : undefined,
                  borderRadius: (isSimplified && isMobile) ? '0 !important' : undefined,
                },
                '&.MuiDialog-paperWidthFalse': {
                  borderRadius: (isSimplified && isMobile) ? '0 !important' : undefined,
                },
                '&.MuiDialog-paperFullScreen': {
                  margin: '0 !important',
                  width: '100vw !important',
                  height: '100vh !important',
                  maxWidth: '100vw !important',
                  maxHeight: '100vh !important',
                  borderRadius: '0 !important',
                },
              },
            }}
          >
            <DialogContent
              sx={{
                p: (isSimplified && isMobile) ? 0 : (isMobile ? 2 : 1),
                overflowY: 'auto',
                backgroundColor: (isSimplified && isMobile) ? '#ffffff' : '#f5f5f5',
                minHeight: (isSimplified && isMobile) ? 'calc(100vh - 100px)' : '500px',  // Reserve space for buttons
                height: (isSimplified && isMobile) ? 'calc(100vh - 100px)' : 'auto',
                maxHeight: (isSimplified && isMobile) ? 'calc(100vh - 100px)' : 'none',
                display: (isSimplified && isMobile) ? 'flex' : 'block',
                flexDirection: (isSimplified && isMobile) ? 'column' : 'row',
                '&.MuiDialogContent-root': {
                  padding: (isSimplified && isMobile) ? 0 : undefined,
                  overflow: (isSimplified && isMobile) ? 'auto' : undefined,
                },
              }}
            >
            {action === 'Onboarding' && (
              <OnboardEmployee 
                onSave={handleOnboardSave} 
                onClose={() => {
                  handleCloseModal();  // Remove setTimeout delay
                }} 
              />
            )}
            {action === 'Archive' && <SimpleBatchArchiveEmployees onClose={handleCloseModal} />}
            {action === 'Reinstate' && <SimpleReinstateEmployees onClose={handleCloseModal} />}
            {/* Suspension feature hidden but not deleted */}
            {/* {action === 'Suspend' && <Suspend />} */}
            {action === 'Disciplinary' && <DisciplinaryRecords />}
          </DialogContent>
          {!isSimplified && (
            <DialogActions
              sx={{
                p: isMobile ? 2 : 3,
              }}
            >
              <Button 
                onClick={handleCloseModal} 
                color="secondary"
                disabled={processing}
                sx={{ 
                  color: '#4B5563', 
                  borderColor: '#D1D5DB',
                  '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB' },
                  textTransform: 'none'
                }}
              >
                Close
              </Button>
            </DialogActions>
          )}
        </Dialog>
        );
      })}
      
      {/* Pay Change Acknowledgement Modal */}
      <PayChangeAcknowledgement 
        open={showAcknowledgement} 
        onClose={() => {
          // Dismiss only; do not clear pending
          setShowAcknowledgement(false);
        }}
        onAcknowledged={() => {
          // After successful acknowledgement, close and refresh counts
          setShowAcknowledgement(false);
          checkPendingAcknowledgements();
        }} 
      />
    </>
  );
};

export default Dashboard;
